import { useMemo } from 'react';
import type { Tramite } from '@/types';
import type { TramiteStatusResult, TramiteStatusConfig, TramiteStatusType } from '@/components/TramiteStatus/types';
import { getColorStatusClasses } from '@/utils/getColorStatusClasses';
import { useTramiteStatusSummary } from '@/components/TramiteStatusSummary/useTramiteStatusSummary';

const STATUS_CONFIGS: Record<TramiteStatusType, Omit<TramiteStatusConfig, 'className'>> = {
  'finalizado': {
    label: 'Finalizado',
    variant: 'default',
    // description: 'El trámite ha sido completado exitosamente' //uncomment if needed (show tooltip)
  },
  'en proceso': {
    label: 'En Proceso',
    variant: 'secondary',
  },
  'pendiente': {
    label: 'Pendiente',
    variant: 'secondary',
  },
  'ok': {
    label: 'OK',
    variant: 'default',
  }
};

export function useTramiteStatus(tramite: Tramite): TramiteStatusResult {
  return useMemo(() => {
    let status = tramite.estado;
    let normalizedStatus = status.toLowerCase() as TramiteStatusType;

    // Get completion counts
    const { completed, totalEnabled } = useTramiteStatusSummary(tramite);

    // Check if all enabled subtramites are completed
    const isVisuallyComplete = totalEnabled > 0 && completed === totalEnabled;

    if (isVisuallyComplete && normalizedStatus !== 'finalizado') {
      status = 'Finalizado';
      normalizedStatus = 'finalizado';
    }

    // Get base config or create default for unknown statuses
    const baseConfig = STATUS_CONFIGS[normalizedStatus] || {
      label: status,
      variant: 'outline' as const,
      description: `Estado: ${status}`
    };

    // Get color classes from existing utility
    const colorClasses = getColorStatusClasses(status);

    const config: TramiteStatusConfig = {
      ...baseConfig,
      className: colorClasses
    };

    const displayText = config.label;

    return {
      status,
      config,
      displayText
    };
  }, [tramite.estado, tramite]);
}

export default useTramiteStatus;